import { useCallback, useEffect, useState } from 'react';
import { apiFetch } from '../../lib/api-client.js';
import type { FinancePeriod } from './use-finance-summary.js';

export interface BreakEvenCurrent {
  revenue: number;
  fixed_costs: number;
  variable_costs: number;
  contribution_margin: number;
  break_even_revenue: number;
  actual_vs_break_even: number;
}

export interface BreakEvenScenario {
  name: string;
  revenue: number;
  fixed: number;
  variable: number;
  net: number;
}

export interface SensitivityPoint {
  revenue_change_pct: number;
  revenue: number;
  net: number;
}

export interface CostItem {
  label: string;
  amount: number;
  source: string;
}

export interface BreakEvenData {
  period: FinancePeriod;
  current: BreakEvenCurrent | null;
  scenarios: BreakEvenScenario[];
  sensitivity: SensitivityPoint[];
  cost_breakdown: { fixed: CostItem[]; variable: CostItem[] };
}

interface Response extends BreakEvenData { error?: string; }

export function useFinanceBreakEven(periodType: string) {
  const [data, setData] = useState<BreakEvenData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      const params = new URLSearchParams();
      if (periodType) params.set('period', periodType);
      const resp = await apiFetch<Response>(`/api/finance/break-even?${params.toString()}`);
      if (resp?.error) throw new Error(resp.error);
      setData(resp);
    } catch (err) { setData(null); setError(err instanceof Error ? err.message : 'Break-even load failed'); }
    finally { setLoading(false); }
  }, [periodType]);

  useEffect(() => { load(); }, [load]);
  return { data, loading, error, refresh: load };
}
